import { hash32, seedFromString } from '@gardener/core';
import type { HatKey } from './hats';
import { DEFAULT_LOOK, LOOK_SLOTS, LOOKS, type Look, lookOptions } from './looks';

/** What a head sets of a look: every slot but the outfit. */
export type Face = Omit<Look, 'outfit'>;
export type FaceSlot = keyof Face;
/** The face's slots, in the look's order. */
export const FACE_SLOTS = [
  'skin',
  'hair',
  'hairColour',
  'eyes',
  'mouth',
  'beard',
  'extras',
] as const satisfies readonly FaceSlot[];

/** A face: the default look's (`DEFAULT_LOOK`), but for `f`. */
const face = (f: Partial<Face>): Face => {
  const { outfit: _, ...d } = DEFAULT_LOOK;
  return { ...d, ...f };
};

interface HeadDef {
  name: string;
  hat: HatKey;
  face: Face;
  /** Unlocked by playing (at least this many rounds or wins, from the career's stats), as well as sold. */
  unlock?: { rounds?: number; wins?: number };
}

/**
 * Heads (ADR-0112): who your gardener is, a premade piece like a Jetpack Joyride costume's head: a hat (`HATS`) and a
 * face (skin, hairstyle, hair colour, eyes, mouth, beard and extras) picked together, never one by one. The wardrobe and
 * the Store deal in heads; a gardener is still drawn, carried and stored as its hat and look (the roster's `hat` and
 * packed `look`), so a head is only what the account may wear (`headOf`). Free (a key missing from `PRICES.head`):
 * everyday faces under the straw hat (`SEEDED_HEADS`; the Store lists one, `isEveryday`) and the headwear of its own
 * (ADR-0089). Sold: characters, each in a hat of its own colour, the hat recolours (also won by playing, `unlock`) and
 * the sets' heads, keyed like their set (content `SETS`). Won: a season's set's head (ADR-0123, `seasonItem`: never
 * sold). The order is the wardrobe's.
 */
export const HEADS = {
  sunny: { name: 'Sunny', hat: 'straw', face: face({}) },
  sprout: {
    name: 'Sprout',
    hat: 'straw',
    face: face({ skin: 'fair', hair: 'pigtails', hairColour: 'blonde', eyes: 'lashes', mouth: 'grin' }),
  },
  basil: {
    name: 'Basil',
    hat: 'straw',
    face: face({ skin: 'brown', hair: 'short', hairColour: 'black', eyes: 'happy', beard: 'moustache' }),
  },
  ivy: { name: 'Ivy', hat: 'straw', face: face({ skin: 'ivory', hair: 'long', hairColour: 'black', eyes: 'almond' }) },
  clover: {
    name: 'Clover',
    hat: 'straw',
    face: face({ skin: 'umber', hair: 'afro', hairColour: 'black', eyes: 'happy', mouth: 'grin' }),
  },
  juniper: {
    name: 'Juniper',
    hat: 'straw',
    face: face({ skin: 'deep', hair: 'braids', hairColour: 'black', eyes: 'lashes' }),
  },
  rowan: {
    name: 'Rowan',
    hat: 'straw',
    face: face({ skin: 'tan', hair: 'curly', hairColour: 'ginger', beard: 'stubble' }),
  },
  hazel: {
    name: 'Hazel',
    hat: 'straw',
    face: face({ skin: 'bronze', hair: 'bun', eyes: 'sleepy', mouth: 'cat' }),
  },
  oakley: {
    name: 'Oakley',
    hat: 'straw',
    face: face({ skin: 'deep', hair: 'buzz', hairColour: 'black', mouth: 'grin', beard: 'goatee' }),
  },
  fern: {
    name: 'Fern',
    hat: 'straw',
    face: face({ skin: 'ivory', hair: 'topknot', hairColour: 'black', eyes: 'monolid' }),
  },
  minty: {
    name: 'Minty',
    hat: 'mint',
    face: face({ skin: 'fair', hair: 'short', hairColour: 'ginger', eyes: 'happy', mouth: 'tongue' }),
  },
  lotus: {
    name: 'Lotus',
    hat: 'conical',
    face: face({ skin: 'tan', hair: 'long', hairColour: 'black', eyes: 'almond' }),
  },
  cedar: {
    name: 'Cedar',
    hat: 'durag',
    face: face({ skin: 'deep', hair: 'buzz', hairColour: 'black', beard: 'stubble', extras: 'earring' }),
  },
  sage: {
    name: 'Sage',
    hat: 'turban',
    face: face({ skin: 'brown', hair: 'buzz', hairColour: 'black', beard: 'full' }),
  },
  lavender: {
    name: 'Lavender',
    hat: 'lavender',
    face: face({ skin: 'fair', hair: 'long', hairColour: 'silver', eyes: 'lashes' }),
    unlock: { wins: 10 },
  },
  nightshade: {
    name: 'Nightshade',
    hat: 'midnight',
    face: face({ skin: 'ivory', hair: 'sleek', hairColour: 'black', eyes: 'sleepy', mouth: 'cat', extras: 'shades' }),
    unlock: { rounds: 25 },
  },
  marigold: {
    name: 'Marigold',
    hat: 'golden',
    face: face({ skin: 'tan', hair: 'short', hairColour: 'black', eyes: 'happy', mouth: 'grin', extras: 'chain' }),
    unlock: { wins: 50 },
  },
  punk: {
    name: 'Punk',
    hat: 'mint',
    face: face({ skin: 'fair', hair: 'puffs', hairColour: 'pink', eyes: 'happy', mouth: 'tongue', extras: 'earring' }),
  },
  biker: {
    name: 'Biker',
    hat: 'durag',
    face: face({ skin: 'tan', hair: 'buzz', hairColour: 'black', beard: 'handlebar', extras: 'shades' }),
  },
  diva: {
    name: 'Diva',
    hat: 'lavender',
    face: face({ skin: 'umber', hair: 'long', hairColour: 'pink', eyes: 'lashes', mouth: 'cat', extras: 'earring' }),
  },
  rockstar: {
    name: 'Rockstar',
    hat: 'midnight',
    face: face({ skin: 'ivory', hair: 'long', hairColour: 'black', eyes: 'sleepy', mouth: 'tongue', extras: 'chain' }),
  },
  // The sets' heads (ADR-0112), keyed like their set and their hat.
  gnome: {
    name: 'Gnome',
    hat: 'gnome',
    face: face({ hair: 'buzz', hairColour: 'silver', eyes: 'happy' }),
  },
  beekeeper: { name: 'Beekeeper', hat: 'beekeeper', face: face({ skin: 'tan', hair: 'short' }) },
  astronaut: {
    name: 'Astronaut',
    hat: 'astronaut',
    face: face({ skin: 'brown', hair: 'short', hairColour: 'black', eyes: 'happy', mouth: 'grin' }),
  },
  pumpkin: { name: 'Pumpkin', hat: 'pumpkin', face: face({}) },
  // A season's head (ADR-0123): only the pass gives it, never sold. A pale face in the hood's shade.
  reaper: { name: 'Reaper', hat: 'reaper', face: face({ skin: 'ivory', eyes: 'sleepy', mouth: 'smile' }) },
  // The Creator Program's head (ADR-0138, content `PROGRAM_ITEMS`): a backwards cap under a headset; the cap's art
  // draws the fringe in its opening (black, short) and hides the rest of the hair.
  creator: {
    name: 'Creator',
    hat: 'creator',
    face: face({ skin: 'brown', hair: 'short', hairColour: 'black', mouth: 'grin' }),
  },
  // The mythic sets' heads (ADR-0160), keyed like their set and their hat, in preview (content `SETS`). Each covers the
  // whole head (a mask), so its face is the default one: nothing of it shows.
  inferno: { name: 'Inferno', hat: 'inferno', face: face({}) },
  kitsune: { name: 'Kitsune', hat: 'kitsune', face: face({}) },
  voidwalker: { name: 'Voidwalker', hat: 'voidwalker', face: face({}) },
  tempest: { name: 'Tempest', hat: 'tempest', face: face({}) },
  glacier: { name: 'Glacier', hat: 'glacier', face: face({}) },
  angler: { name: 'Abyssal Angler', hat: 'angler', face: face({}) },
  jester: { name: 'Jester', hat: 'jester', face: face({}) },
  plague: { name: 'Plague Doctor', hat: 'plague', face: face({}) },
  chronomancer: { name: 'Chronomancer', hat: 'chronomancer', face: face({}) },
  glitch: { name: 'Glitch', hat: 'glitch', face: face({}) },
  disco: { name: 'Disco', hat: 'disco', face: face({}) },
  origami: { name: 'Origami', hat: 'origami', face: face({}) },
  ooze: { name: 'Ooze', hat: 'ooze', face: face({}) },
  barnstormer: { name: 'Barnstormer', hat: 'barnstormer', face: face({}) },
  pirate: { name: 'Pirate', hat: 'pirate', face: face({}) },
  magician: { name: 'Magician', hat: 'magician', face: face({}) },
  chef: { name: 'Chef', hat: 'chef', face: face({ eyes: 'happy', mouth: 'grin' }) },
  knight: { name: 'Knight', hat: 'knight', face: face({}) },
  sheriff: { name: 'Sheriff', hat: 'sheriff', face: face({ eyes: 'sleepy', mouth: 'smile' }) },
  luchador: { name: 'Luchador', hat: 'luchador', face: face({ eyes: 'round', mouth: 'grin' }) },
} as const satisfies Record<string, HeadDef>;

export type HeadKey = keyof typeof HEADS;
/** Every head, in the wardrobe's order. */
export const HEAD_KEYS = Object.keys(HEADS) as HeadKey[];
export const isHeadKey = (key: string): key is HeadKey => Object.hasOwn(HEADS, key);

/**
 * The heads a gardener without one of its own wears (an account until it picks, a bot, a guest; `lookOf`): the everyday
 * faces, free and under the straw hat, so a seeded gardener's hat is the default one everywhere (`straw`).
 */
export const SEEDED_HEADS = [
  'sunny',
  'sprout',
  'basil',
  'ivy',
  'clover',
  'juniper',
  'rowan',
  'hazel',
  'oakley',
  'fern',
] as const satisfies readonly HeadKey[];

/**
 * Is `key` an everyday face (ADR-0112 amended)? Ten faces under one straw hat read as one head on a tile, so the Store
 * lists only one of them: the one you wear, else the one your account was given (`seededHead`).
 */
export const isEveryday = (key: string): boolean => (SEEDED_HEADS as readonly string[]).includes(key);

/** The everyday head `seed` picks (an account's id hashed, a bot's name): its face in `lookOf`. */
export const seededHead = (seed: number): HeadKey =>
  SEEDED_HEADS[hash32(seed, 0) % SEEDED_HEADS.length] as (typeof SEEDED_HEADS)[number];

/** The head a gardener in `hat` and `look` wears, or null: its hat and face are no head's (an old account's own). */
export const headOf = (hat: string, look: Face): HeadKey | null =>
  HEAD_KEYS.find((k) => HEADS[k].hat === hat && FACE_SLOTS.every((s) => HEADS[k].face[s] === look[s])) ?? null;

/** `look` under head `key`: its face, the outfit kept. */
export const withHead = (look: Look, key: HeadKey): Look => ({ ...look, ...HEADS[key].face });

/**
 * What a slot weighs in `closestHead`: the hat over the whole face (it is what reads in a match, and a recolour was
 * earned or bought), then the skin, then what shows most round the face.
 */
const LIKE: Record<FaceSlot | 'hat', number> = {
  hat: 17,
  skin: 6,
  hair: 3,
  hairColour: 2,
  beard: 2,
  extras: 1,
  eyes: 1,
  mouth: 1,
};

/**
 * The head among `among` most like a gardener in `hat` and `look` (a look picked slot by slot before heads, ADR-0112's
 * migration): the most matching slots, by `LIKE` (the same hat first); a tie goes to the first in `among`.
 */
export function closestHead(hat: string, look: Face, among: readonly HeadKey[]): HeadKey {
  let best = among[0] as HeadKey;
  let bestScore = -1;
  for (const k of among) {
    const h = HEADS[k];
    const score =
      (h.hat === hat ? LIKE.hat : 0) + FACE_SLOTS.reduce((n, s) => n + (h.face[s] === look[s] ? LIKE[s] : 0), 0);
    if (score > bestScore) [best, bestScore] = [k, score];
  }
  return best;
}

/**
 * A look picked by `seed` (an account's id hashed, a bot's name): one of the everyday heads' faces (`SEEDED_HEADS`, all
 * under the straw hat), and the overalls, or once in two the hoodie or the shirt (never a set's suit: a costume is
 * bought, ADR-0112), so a crowd varies and still reads as gardeners.
 */
export function lookOf(seed: number): Look {
  const head = seededHead(seed);
  const outfits = lookOptions('outfit');
  const outfit = hash32(seed, 7) % 2 ? outfits[0] : outfits[1 + (hash32(seed, 7, 2) % 2)];
  return { ...HEADS[head].face, outfit } as Look;
}

/**
 * Account `id`'s look: until it picks one, the one its id picks (stable, so nobody starts as a clone); then its own
 * (`own`, as stored) slot by slot, where a slot it lacks or an option this build doesn't know (a slot added since, an
 * option gone) is the slot's first: a look saved before the beard and extras wears none, one saved before the outfit
 * the overalls.
 */
export function accountLook(id: string, own?: Partial<Record<string, string>>): Look {
  if (!own) return lookOf(seedFromString(id));
  return Object.fromEntries(
    LOOK_SLOTS.map((s) => [s, Object.hasOwn(LOOKS[s].options, own[s] ?? '') ? own[s] : DEFAULT_LOOK[s]]),
  ) as Look;
}
